import { releaseSessionFiles } from "@/lib/storage/draft-files-store";
import {
  loadSessionRecord,
  notifySessionRegistryChanged,
  setSessionPaused,
} from "@/lib/storage/session-record-store";
import {
  loadUploadManifest,
  type UploadManifest,
} from "@/lib/storage/upload-manifest-store";
import {
  discardUnresumableUploadSession,
  loadUploadSessionFiles,
} from "@/lib/upload/load-upload-session-files";
import {
  prepareServerSession,
  retryFileUpload,
  runSessionUpload,
} from "@/lib/upload/upload-client";
import { UploadApiError } from "@/lib/upload/upload-api";
import {
  computeSessionUploadProgress,
  deriveUploadPhase,
  type SessionUploadProgress,
  type UploadPhase,
} from "@/lib/upload/upload-progress";

/**
 * Maximum number of upload sessions that run at the same time. Each session
 * already fans out across files and parts, so a small cap keeps several
 * sessions progressing concurrently without saturating the connection.
 */
const MAX_CONCURRENT_SESSION_UPLOADS = 3;

export type SessionUploadStatus = {
  clientSessionId: string;
  progress: SessionUploadProgress | null;
  phase: UploadPhase;
  error: string | null;
  /** Running right now (false while queued behind the concurrency cap). */
  active: boolean;
  /** Waiting for a concurrency slot. */
  queued: boolean;
  /** Source files have been resolved (from memory or local storage). */
  hydrated: boolean;
  /** The user has explicitly paused this upload (sticky across refresh/tabs). */
  paused: boolean;
};

type SessionListener = (status: SessionUploadStatus) => void;

const statuses = new Map<string, SessionUploadStatus>();
const providedFilesById = new Map<string, File[]>();
const sessionListeners = new Map<string, Set<SessionListener>>();
const waitingQueue: string[] = [];
/** Aborts the in-flight run for a session so a pause can stop it immediately. */
const controllers = new Map<string, AbortController>();
let runningCount = 0;

/**
 * Releases the cross-tab upload-ownership lock for a session this tab is the
 * uploader for. Held for the duration of the run so no other tab uploads the
 * same session at the same time (which would thrash: competing multipart
 * inits/completes and overwritten checkpoints).
 */
const ownershipReleasers = new Map<string, () => void>();

function getLockManager(): LockManager | undefined {
  if (typeof navigator === "undefined") return undefined;
  return (navigator as Navigator & { locks?: LockManager }).locks;
}

function uploadLockName(clientSessionId: string) {
  return `surf-clips-ai:upload-owner:${clientSessionId}`;
}

/**
 * How long to wait for the upload-ownership lock before giving up and becoming a
 * viewer. Long enough to cover this tab handing the lock from a just-aborted run
 * to its restart/resume (released in ms), short enough that a viewer of a run
 * genuinely owned by another tab doesn't hold a concurrency slot for long.
 */
const OWNERSHIP_HANDOFF_TIMEOUT_MS = 1500;

/**
 * Tries to become the single tab that runs this session's upload. Resolves true
 * if this tab now holds the lock (release later via {@link releaseUploadOwnership}),
 * or false if another tab still owns it after a short wait. The brief wait lets
 * a restart/resume reclaim the lock from this tab's own aborted run, which is
 * still unwinding. Where the Web Locks API is missing, falls back to true so a
 * lone tab still uploads.
 */
function acquireUploadOwnership(clientSessionId: string): Promise<boolean> {
  const locks = getLockManager();
  if (!locks) return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    const abort = new AbortController();
    const timeoutId = setTimeout(
      () => abort.abort(),
      OWNERSHIP_HANDOFF_TIMEOUT_MS,
    );

    locks
      .request(uploadLockName(clientSessionId), { signal: abort.signal }, () => {
        // Granted: hold the lock until the run finishes by keeping this
        // callback's promise pending; resolving `release` frees it.
        clearTimeout(timeoutId);
        return new Promise<void>((release) => {
          ownershipReleasers.set(clientSessionId, release);
          resolve(true);
        });
      })
      .catch(() => {
        // Either the handoff wait timed out (another tab owns it) or the request
        // failed outright. Either way, become a viewer: uploading anyway would
        // risk two tabs racing the same multipart uploads. A lone tab with no
        // Web Locks support already short-circuits to true above.
        clearTimeout(timeoutId);
        resolve(false);
      });
  });
}

function releaseUploadOwnership(clientSessionId: string) {
  const release = ownershipReleasers.get(clientSessionId);
  if (!release) return;
  ownershipReleasers.delete(clientSessionId);
  release();
}

/**
 * Cross-tab sync. The upload runs in exactly one tab (enforced by the ownership
 * lock above), which broadcasts its status so other tabs viewing the same
 * session update live instead of polling shared storage. Control messages let a
 * viewer tab drive the owner's run (pause/resume/restart) since only the owner
 * holds the abort handle. Statuses from other tabs are kept separate from
 * locally owned ones so they never block this tab's own upload actions.
 */
const PROGRESS_CHANNEL_NAME = "surf-clips-ai:upload-progress";
const BROADCAST_INTERVAL_MS = 250;

type UploadControlAction = "pause" | "resume" | "restart";
type ProgressMessage = {
  type: "progress";
  clientSessionId: string;
  status: SessionUploadStatus;
};
type ControlMessage = {
  type: "control";
  clientSessionId: string;
  action: UploadControlAction;
};
type ChannelMessage = ProgressMessage | ControlMessage;

const remoteStatuses = new Map<string, SessionUploadStatus>();
const progressChannel =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel(PROGRESS_CHANNEL_NAME);

const lastBroadcastAt = new Map<string, number>();
const lastBroadcastPhase = new Map<string, UploadPhase>();
const pendingBroadcast = new Map<string, ReturnType<typeof setTimeout>>();

/** A live local upload is authoritative; otherwise fall back to a remote tab. */
function currentStatus(clientSessionId: string): SessionUploadStatus | null {
  const local = statuses.get(clientSessionId);
  if (local && (local.active || local.queued)) return local;
  return remoteStatuses.get(clientSessionId) ?? local ?? null;
}

function scheduleBroadcast(clientSessionId: string) {
  if (!progressChannel) return;
  const status = statuses.get(clientSessionId);
  if (!status) return;

  const now = Date.now();
  const lastAt = lastBroadcastAt.get(clientSessionId) ?? 0;
  const phaseChanged = lastBroadcastPhase.get(clientSessionId) !== status.phase;

  // Phase changes (start/complete/error) go out immediately; rapid progress
  // ticks are throttled with a trailing flush so other tabs aren't flooded.
  if (phaseChanged || now - lastAt >= BROADCAST_INTERVAL_MS) {
    const pending = pendingBroadcast.get(clientSessionId);
    if (pending) {
      clearTimeout(pending);
      pendingBroadcast.delete(clientSessionId);
    }
    lastBroadcastAt.set(clientSessionId, now);
    lastBroadcastPhase.set(clientSessionId, status.phase);
    progressChannel.postMessage({
      type: "progress",
      clientSessionId,
      status,
    } satisfies ProgressMessage);
    return;
  }

  if (!pendingBroadcast.has(clientSessionId)) {
    const delay = BROADCAST_INTERVAL_MS - (now - lastAt);
    pendingBroadcast.set(
      clientSessionId,
      setTimeout(() => {
        pendingBroadcast.delete(clientSessionId);
        scheduleBroadcast(clientSessionId);
      }, Math.max(0, delay)),
    );
  }
}

function broadcastControl(clientSessionId: string, action: UploadControlAction) {
  progressChannel?.postMessage({
    type: "control",
    clientSessionId,
    action,
  } satisfies ControlMessage);
}

/**
 * Applies a control action that arrived from another tab. Only the tab that
 * owns the run holds its abort handle, so a pause/restart from a viewer tab
 * reaches the owner here. Received controls never re-broadcast (a BroadcastChannel
 * doesn't echo to its sender, and re-posting would ping-pong across 3+ tabs).
 */
function handleRemoteControl(
  clientSessionId: string,
  action: UploadControlAction,
) {
  if (action === "pause") {
    applyPauseLocally(clientSessionId);
    return;
  }
  if (action === "resume") {
    applyResumeLocally(clientSessionId);
    return;
  }
  applyRestartLocally(clientSessionId);
}

progressChannel?.addEventListener("message", (event: MessageEvent<ChannelMessage>) => {
  const message = event.data;
  if (message.type === "control") {
    handleRemoteControl(message.clientSessionId, message.action);
    return;
  }

  const { clientSessionId, status } = message;
  const local = statuses.get(clientSessionId);
  if (local && (local.active || local.queued)) return;
  remoteStatuses.set(clientSessionId, status);
  fireListeners(clientSessionId);
});

function defaultStatus(clientSessionId: string): SessionUploadStatus {
  return {
    clientSessionId,
    progress: null,
    phase: "idle",
    error: null,
    active: false,
    queued: false,
    hydrated: false,
    paused: false,
  };
}

function fireListeners(clientSessionId: string) {
  const status = currentStatus(clientSessionId);
  if (!status) return;
  const listeners = sessionListeners.get(clientSessionId);
  if (listeners) {
    for (const listener of listeners) listener(status);
  }
}

function notify(clientSessionId: string) {
  fireListeners(clientSessionId);
  scheduleBroadcast(clientSessionId);
}

function patchStatus(
  clientSessionId: string,
  patch: Partial<SessionUploadStatus>,
) {
  const current = statuses.get(clientSessionId) ?? defaultStatus(clientSessionId);
  statuses.set(clientSessionId, { ...current, ...patch });
  notify(clientSessionId);
}

function applyProgress(clientSessionId: string, progress: SessionUploadProgress) {
  const { phase, error } = deriveUploadPhase(progress);
  patchStatus(clientSessionId, { progress, phase, error });
}

export function getSessionUploadStatus(
  clientSessionId: string,
): SessionUploadStatus | null {
  return currentStatus(clientSessionId);
}

export function isSessionUploadActive(clientSessionId: string) {
  return currentStatus(clientSessionId)?.active ?? false;
}

export function subscribeSessionUpload(
  clientSessionId: string,
  listener: SessionListener,
) {
  let listeners = sessionListeners.get(clientSessionId);
  if (!listeners) {
    listeners = new Set();
    sessionListeners.set(clientSessionId, listeners);
  }
  listeners.add(listener);

  return () => {
    const set = sessionListeners.get(clientSessionId);
    if (!set) return;
    set.delete(listener);
    if (set.size === 0) sessionListeners.delete(clientSessionId);
  };
}

/**
 * Creates the server session up front (so the share link is available
 * immediately) without starting the byte transfer. Used while clips are still
 * saving to the device; the upload itself begins later via
 * {@link ensureSessionUpload}, which reuses this session. Safe to call
 * repeatedly and alongside the upload start: session creation is deduped.
 */
export async function prepareSessionUpload(
  clientSessionId: string,
  files: File[],
) {
  providedFilesById.set(clientSessionId, files);

  const existing = statuses.get(clientSessionId);
  if (existing && (existing.active || existing.queued)) return;

  try {
    await prepareServerSession(clientSessionId, files);
  } catch (error) {
    console.error("Failed to prepare upload session", error);
    // Surface the failure (the share link won't exist yet) without blocking the
    // upload: starting it later clears this error and recreates the session.
    patchStatus(clientSessionId, {
      error: error instanceof Error ? error.message : "Failed to prepare upload",
    });
  }
}

/**
 * Tears down all in-memory upload state for a session and stops any in-flight
 * run this tab owns. Used when a session is deleted so the upload can neither
 * keep running nor recreate local state behind the deletion. Deleting the
 * controller first makes the aborted run treat itself as superseded, so its
 * unwind won't re-seed a status entry for the removed session.
 */
export function discardSessionUpload(clientSessionId: string) {
  const queuedIndex = waitingQueue.indexOf(clientSessionId);
  if (queuedIndex !== -1) waitingQueue.splice(queuedIndex, 1);

  const controller = controllers.get(clientSessionId);
  controllers.delete(clientSessionId);
  controller?.abort();
  releaseUploadOwnership(clientSessionId);

  statuses.delete(clientSessionId);
  remoteStatuses.delete(clientSessionId);
  providedFilesById.delete(clientSessionId);

  const pending = pendingBroadcast.get(clientSessionId);
  if (pending) {
    clearTimeout(pending);
    pendingBroadcast.delete(clientSessionId);
  }
  lastBroadcastAt.delete(clientSessionId);
  lastBroadcastPhase.delete(clientSessionId);

  fireListeners(clientSessionId);
}

/**
 * Ensures the given session is uploading (now or queued). Safe to call
 * repeatedly: an already running, queued, or completed session is left alone.
 * Pass `files` when they are already in memory (e.g. right after grouping) to
 * avoid a storage round-trip; otherwise they are loaded from local storage.
 */
export function ensureSessionUpload(clientSessionId: string, files?: File[]) {
  if (files) providedFilesById.set(clientSessionId, files);

  const existing = statuses.get(clientSessionId);
  // A paused upload must stay paused: re-renders, the orchestrator, and the
  // autostart effect all call this, so the in-memory flag is the gate that
  // keeps them from silently un-pausing it. Resuming clears the flag first.
  if (existing?.paused) return;
  if (
    existing &&
    (existing.active || existing.queued || existing.phase === "complete")
  ) {
    return;
  }

  patchStatus(clientSessionId, {
    queued: true,
    active: false,
    error: null,
    phase: existing?.phase === "complete" ? "complete" : "uploading",
  });
  waitingQueue.push(clientSessionId);
  drainQueue();
}

function drainQueue() {
  while (runningCount < MAX_CONCURRENT_SESSION_UPLOADS && waitingQueue.length > 0) {
    const clientSessionId = waitingQueue.shift();
    if (!clientSessionId) continue;
    void startSessionUpload(clientSessionId);
  }
}

/**
 * Stops this tab's run for a session: drop any queued slot, abort the in-flight
 * run (completed parts are already checkpointed, so no progress is lost), and
 * mark it paused. Shared by the local pause and a pause arriving from another
 * tab; only the tab that owns the run holds the abort handle, so this is a
 * no-op (beyond the display flag) on viewer tabs.
 */
function applyPauseLocally(clientSessionId: string) {
  const queuedIndex = waitingQueue.indexOf(clientSessionId);
  if (queuedIndex !== -1) waitingQueue.splice(queuedIndex, 1);

  controllers.get(clientSessionId)?.abort();

  patchStatus(clientSessionId, {
    paused: true,
    active: false,
    queued: false,
  });
}

/**
 * Pauses a session's upload. The flag is persisted so the pause survives a
 * refresh and other tabs, the in-flight run is aborted, and any queued slot is
 * released. The control is broadcast so the tab that actually owns the run
 * (which may not be this one) aborts too. Resuming continues from the last
 * completed part.
 */
export async function pauseSessionUpload(clientSessionId: string) {
  await setSessionPaused(clientSessionId, true);
  applyPauseLocally(clientSessionId);
  broadcastControl(clientSessionId, "pause");
  notifySessionRegistryChanged();
}

/**
 * Seeds the display status from the persisted manifest without starting an
 * upload, so a paused (or not-yet-running) session shows each clip's real
 * progress from its completed parts on load instead of resetting to zero.
 */
export async function hydrateSessionUpload(clientSessionId: string) {
  const existing = statuses.get(clientSessionId);
  // A live or queued run, or an already-seeded status, owns the progress.
  if (existing && (existing.active || existing.queued || existing.progress)) {
    return;
  }

  const [manifest, record] = await Promise.all([
    loadUploadManifest(clientSessionId),
    loadSessionRecord(clientSessionId),
  ]);
  if (!manifest) return;

  // A run may have started while awaiting storage; don't clobber its progress.
  const current = statuses.get(clientSessionId);
  if (current && (current.active || current.queued || current.progress)) {
    return;
  }

  const progress = computeSessionUploadProgress(manifest);
  const { phase, error } = deriveUploadPhase(progress);
  patchStatus(clientSessionId, {
    progress,
    phase,
    error,
    paused: record?.paused ?? false,
    hydrated: true,
  });
}

/** Clears the paused flag locally and tries to (re)start the upload here. */
function applyResumeLocally(clientSessionId: string) {
  patchStatus(clientSessionId, { paused: false });
  ensureSessionUpload(clientSessionId);
}

/**
 * Clears the paused flag and re-queues the upload to resume from checkpoint.
 * Broadcast so every tab clears its paused display and races to become the
 * uploader; the ownership lock ensures exactly one wins.
 */
export async function resumeSessionUpload(clientSessionId: string) {
  await setSessionPaused(clientSessionId, false);
  applyResumeLocally(clientSessionId);
  broadcastControl(clientSessionId, "resume");
}

/**
 * Force-restarts a stuck run: abort this tab's in-flight run (if it owns one)
 * and re-queue from the last checkpoint. A stalled run is still "active", so
 * {@link ensureSessionUpload} alone would no-op. Shared by the local restart and
 * a restart arriving from another tab.
 */
function applyRestartLocally(clientSessionId: string) {
  const queuedIndex = waitingQueue.indexOf(clientSessionId);
  if (queuedIndex !== -1) waitingQueue.splice(queuedIndex, 1);

  // Abort the in-flight (stuck) run so its files reset to pending and unwind;
  // the serialized re-queue below then starts a fresh run from checkpoint.
  controllers.get(clientSessionId)?.abort();

  patchStatus(clientSessionId, {
    paused: false,
    active: false,
    queued: false,
  });
  ensureSessionUpload(clientSessionId);
}

/**
 * Kicks a stalled upload back into motion now, even when the run is owned by a
 * different tab. The "Paused, resumes automatically" manual Resume calls this so
 * the click responds immediately instead of waiting on the stall to clear.
 */
export async function restartSessionUpload(clientSessionId: string) {
  await setSessionPaused(clientSessionId, false);
  applyRestartLocally(clientSessionId);
  broadcastControl(clientSessionId, "restart");
}

async function finalizeRun(clientSessionId: string, manifest: UploadManifest) {
  const progress = computeSessionUploadProgress(manifest);
  const { phase, error } = deriveUploadPhase(progress);

  if (phase === "complete") {
    try {
      await releaseSessionFiles(clientSessionId);
    } catch (cleanupError) {
      console.error("Failed to release session files", cleanupError);
    }
    providedFilesById.delete(clientSessionId);
  }

  patchStatus(clientSessionId, { progress, phase, error });
  notifySessionRegistryChanged();
}

async function startSessionUpload(clientSessionId: string) {
  // Reserve the concurrency slot synchronously so drainQueue's accounting holds
  // (acquiring ownership below is async).
  runningCount += 1;

  const controller = new AbortController();
  let owned = false;

  try {
    // Single-writer across tabs: only the lock holder runs the transfer. If
    // another tab owns it, this tab stays a viewer and mirrors its broadcast
    // status instead of competing for the same multipart uploads.
    owned = await acquireUploadOwnership(clientSessionId);
    if (!owned) {
      patchStatus(clientSessionId, { active: false, queued: false });
      return;
    }
    // A pause may have landed between queueing and acquiring ownership.
    if (statuses.get(clientSessionId)?.paused) {
      patchStatus(clientSessionId, { active: false, queued: false });
      return;
    }

    patchStatus(clientSessionId, { active: true, queued: false, phase: "uploading", error: null });
    controllers.set(clientSessionId, controller);

    let files = providedFilesById.get(clientSessionId);
    if (!files) {
      const loaded = await loadUploadSessionFiles(clientSessionId);
      if (!loaded) {
        await discardUnresumableUploadSession(clientSessionId);
        patchStatus(clientSessionId, {
          active: false,
          hydrated: true,
          phase: "error",
          error: "These clips are no longer available on this device.",
        });
        notifySessionRegistryChanged();
        return;
      }
      files = loaded;
      providedFilesById.set(clientSessionId, loaded);
    }

    patchStatus(clientSessionId, { hydrated: true });

    const manifest = await runSessionUpload({
      clientSessionId,
      files,
      signal: controller.signal,
      onProgress: (progress) => applyProgress(clientSessionId, progress),
    });

    await finalizeRun(clientSessionId, manifest);
  } catch (error) {
    // A pause aborts in flight; the manager already cleared active, and a resume
    // may already own this session, so don't touch its status here. Genuine
    // failures still surface an error (unless a newer run has taken over).
    const supersededByResume =
      controllers.get(clientSessionId) !== controller;
    if (
      !(error instanceof DOMException && error.name === "AbortError") &&
      !supersededByResume
    ) {
      patchStatus(clientSessionId, {
        active: false,
        phase: "error",
        error: error instanceof Error ? error.message : "Upload failed",
      });
      notifySessionRegistryChanged();
    }
  } finally {
    // A resume that races this run's unwind installs a new controller; only
    // clear our own so we never strip the freshly resumed run's abort handle or
    // mark its active upload as stopped.
    if (controllers.get(clientSessionId) === controller) {
      controllers.delete(clientSessionId);
      patchStatus(clientSessionId, { active: false });
    }
    if (owned) releaseUploadOwnership(clientSessionId);
    runningCount -= 1;
    drainQueue();
  }
}

export async function retrySessionFile(clientSessionId: string, file: File) {
  const manifest = await loadUploadManifest(clientSessionId);
  if (!manifest) {
    ensureSessionUpload(clientSessionId);
    return;
  }

  patchStatus(clientSessionId, { active: true, phase: "uploading", error: null });

  // A dead session token (401) can't be fixed per-file: the whole session must
  // be re-created, which resets every file. Fall back to a full session run,
  // which re-creates the session and re-uploads all files.
  let sessionGone = false;
  try {
    const updated = await retryFileUpload(file, manifest, {
      onProgress: (progress) => applyProgress(clientSessionId, progress),
    });
    await finalizeRun(clientSessionId, updated);
  } catch (error) {
    if (error instanceof UploadApiError && error.status === 401) {
      sessionGone = true;
    } else {
      patchStatus(clientSessionId, {
        active: false,
        phase: "error",
        error: error instanceof Error ? error.message : "Retry failed",
      });
      notifySessionRegistryChanged();
    }
  } finally {
    patchStatus(clientSessionId, { active: false });
  }

  // Triggered after the finally so the full run's own status updates win.
  if (sessionGone) {
    ensureSessionUpload(clientSessionId);
  }
}
